import React from 'react';
import * as LucideIcons from 'lucide-react';
import { HelpCircle } from 'lucide-react';

export type IconName = keyof typeof LucideIcons;

interface IconProps extends React.SVGProps<SVGSVGElement> {
    name: IconName;
    size?: number;
    color?: string;
    className?: string;
    strokeWidth?: number;
}

function Icon({
    name,
    size = 24,
    color = "currentColor",
    className = "",
    strokeWidth = 2,
    ...props
}: IconProps) {
    const IconComponent = LucideIcons[name] as React.ComponentType<any>;

    // Icons are decorative BY DEFAULT. lucide-react renders a bare <svg> and adds no
    // aria-hidden, so every icon in the app was exposed to screen readers as an unnamed
    // graphic — 279 of them in the tournament module alone. Anything that is genuinely
    // meaningful names itself (aria-label / aria-labelledby / an explicit role), and that
    // always wins here: a named icon must stay in the accessibility tree.
    const isNamed =
        props['aria-label'] != null ||
        props['aria-labelledby'] != null ||
        (props.role != null && props.role !== 'presentation' && props.role !== 'none');
    const a11y = props['aria-hidden'] != null
        ? {}
        : isNamed ? {} : { 'aria-hidden': true as const };

    if (!IconComponent) {
        return <HelpCircle size={size} color="gray" strokeWidth={strokeWidth} className={className} {...a11y} {...props} />;
    }

    return <IconComponent
        size={size}
        color={color}
        strokeWidth={strokeWidth}
        className={className}
        {...a11y}
        {...props}
    />;
}
export default Icon;